import React from 'react';
import { trackEvent } from '../config/site';
import { Check, ArrowRight, ShieldCheck } from 'lucide-react';

interface PlansSectionProps {
  onOpenWaitlist: (selectedPlan?: string) => void;
}

export const PlansSection: React.FC<PlansSectionProps> = ({ onOpenWaitlist }) => {
  const handleSelectPlan = (planName: string) => {
    trackEvent('plans_cta', { plan: planName });
    onOpenWaitlist(planName);
  };

  const plans = [
    {
      name: 'Starter',
      credits: '700',
      tagline: 'Para especialistas e criadores estruturando seu 1º infoproduto.',
      features: [
        '700 créditos de compilação por mês',
        'Pesquisa de mercado & blueprint pedagógico',
        'Ebook Master completo (PDF diagramado + EPUB)',
        'Workbook prático de acompanhamento',
        'Roteiro de VSL em 3 atos',
        'Exportação de pacotes em ZIP verificado',
        'Isolamento de tenancy & dados privados'
      ],
      ctaText: 'Solicitar Convite Starter',
      highlighted: false
    },
    {
      name: 'Pro',
      credits: '1.700',
      badge: 'Mais Escolhido',
      tagline: 'Para coprodutores e criadores com lançamentos frequentes.',
      features: [
        '1.700 créditos de compilação por mês',
        'Tudo do plano Starter incluso',
        'Sequência estratégica de 5 a 7 e-mails',
        'Copy completa para página de vendas',
        'Biblioteca de prompts práticos para alunos',
        'Roteiros para carrosséis educativos',
        'Fila de processamento com prioridade',
        'Suporte prioritário via canal direto'
      ],
      ctaText: 'Solicitar Convite Pro',
      highlighted: true
    },
    {
      name: 'Premium',
      credits: '3.000',
      tagline: 'Para agências, coprodutoras e operações de escala.',
      features: [
        '3.000 créditos de compilação por mês',
        'Tudo do plano Pro incluso',
        'Capacidade para múltiplos produtos simultâneos',
        'Refinamento de voz de marca customizado',
        'Suporte técnico de engenharia dedicado',
        'Consultoria de arquitetura de catálogo',
        'Acesso antecipado a novos módulos de roadmap'
      ],
      ctaText: 'Solicitar Convite Premium',
      highlighted: false
    }
  ];

  return (
    <section id="planos" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary px-3 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/20">
            Acesso Controlado
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Planos & Franquia de Créditos
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Consumo transparente e atômico. Você utiliza créditos conforme o avanço de cada estágio verificado do pipeline.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`rounded-3xl p-8 flex flex-col justify-between transition-all duration-300 relative ${
                plan.highlighted
                  ? 'bg-gradient-to-b from-brand-surface to-brand-surface border-2 border-brand-primary shadow-2xl shadow-brand-primary/15 md:-translate-y-2'
                  : 'bg-brand-surface border border-brand-border hover:border-brand-primary/40'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-brand-primary to-brand-cyan text-white text-[11px] font-bold tracking-wider uppercase shadow-md">
                  {plan.badge}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  <span className="text-xs font-semibold text-brand-cyan bg-brand-cyan/10 px-2.5 py-1 rounded-lg border border-brand-cyan/20">
                    {plan.credits} créditos/mês
                  </span>
                </div>

                <p className="text-xs text-brand-textMuted min-h-[32px] mb-6 leading-relaxed">
                  {plan.tagline}
                </p>

                <div className="py-4 border-y border-brand-border/60 mb-6">
                  <span className="text-xs text-brand-textSubtle block">Condição de Acesso:</span>
                  <span className="text-base font-bold text-white flex items-center gap-1.5 mt-0.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    Exclusivo para Convidados do Beta
                  </span>
                </div>

                <div className="space-y-3 mb-8">
                  <span className="text-xs font-bold text-brand-text uppercase tracking-wider block">
                    O que está incluso:
                  </span>
                  {plan.features.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-2.5 text-xs text-brand-textMuted">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleSelectPlan(plan.name)}
                className={`w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  plan.highlighted
                    ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg shadow-brand-primary/25 hover:shadow-brand-primary/40 hover:scale-[1.02]'
                    : 'bg-brand-bg hover:bg-brand-surfaceLight text-white border border-brand-border hover:border-brand-borderHover'
                }`}
              >
                <span>{plan.ctaText}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Footnote */}
        <div className="mt-12 text-center text-xs text-brand-textSubtle max-w-2xl mx-auto">
          * Durante a fase de Private Beta, a atribuição de créditos e cotas é realizada diretamente com nossa equipe de produto aos criadores selecionados.
        </div>
      </div>
    </section>
  );
};
